import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IncidentType } from '@/types';

import { useFilteredIncidents } from '@/hooks/use-filtered-incidents';
import { useGetIncidentListData } from '@/hooks/use-get-incident-list-data';
import useIncidentSearch from '@/hooks/use-incident-search';
import getIncidentLanguage from '@/utils/helpers/get-incident-language';

export const useRelatedIncidents = (matchedIncidents: IncidentType[]) => {
  const { i18n } = useTranslation();
  const { searchKey } = useIncidentSearch();
  const incidentLang = getIncidentLanguage(i18n.language);

  const { data: allIncidents = [] } = useGetIncidentListData();

  const otherRelatedIncidents = useMemo(() => {
    const matchedIds = new Set(matchedIncidents.map((incident) => incident.gtfsAlertId));

    return allIncidents.filter((incident: IncidentType) => !matchedIds.has(incident.gtfsAlertId));
  }, [allIncidents, matchedIncidents]);

  const filteredMatchedIncidents = useFilteredIncidents(matchedIncidents, searchKey, incidentLang);
  const filteredOtherRelatedIncidents = useFilteredIncidents(
    otherRelatedIncidents,
    searchKey,
    incidentLang
  );

  const isEmpty =
    !filteredMatchedIncidents?.length &&
    !filteredOtherRelatedIncidents?.length &&
    !!searchKey?.trim();

  return { filteredMatchedIncidents, filteredOtherRelatedIncidents, isEmpty };
};
